/*!
 * MOTUS · snazzy-facets.js  (v3 déterministe URL-first)
 * Snazzy Digital Agency
 *
 * ARCHITECTURE :
 * - Le SERVEUR fait du best-effort avec les filtres Shopify (Liquid ne
 *   peut pas lire request.url ni parser les query params).
 * - Le CLIENT lit location.search — source de vérité — et :
 *   1) marque input.checked pour toutes les valeurs présentes dans
 *      l'URL, indépendamment de ce que Shopify rapporte.
 *   2) applique la règle "actif ssi TOUTES les valeurs cochées" sur
 *      chaque pastille / libellé style.
 *   3) hijacke les clicks × sur les chips pour rebâtir l'URL depuis
 *      location.search en retirant TOUTES les valeurs du groupe
 *      (source manifest métaobjet exposé par le serveur).
 *   4) rend un CHIP CORRIGÉ si le serveur n'a pas rendu de chip pour
 *      un groupe pourtant pleinement actif dans l'URL.
 * - Pas de subset/tie-break : règle "toutes requises" mutuellement
 *   exclusive entre groupes disjoints.
 *
 * DEBUG (?snazzy_debug=1) : console.table + log des clics.
 */
(function () {
  "use strict";

  var SELECTOR_BTN = "[data-snazzy-groupe-toggle]";
  var SELECTOR_GROUPE = ".snazzy-batch-group";
  var SELECTOR_VALUE = ".snazzy-value-input";
  var SELECTOR_FORM = 'form[is="facets-form"]';
  var DEBUG = /[?&]snazzy_debug=1(?:&|$)/.test(location.search);

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[snazzy]");
    console.log.apply(console, args);
  }

  // ==== DEDUP URL (facet:update capture) =================================
  // Contexte : <facets-drawer> extends Modal avec shouldAppendToBody=true.
  // Apres AJAX section swap, l'ancien drawer reste dans <body> ET le
  // nouveau s'ajoute. Le DOM contient 2 forms + doublons d'inputs.
  // Consequence : chaque input.checked envoie sa valeur 2x dans l'URL
  // (22 187 chars pour Blanc+Bleu+Rose = 504 params dont 142 doublons).
  //
  // Solution : intercepter facet:update en phase CAPTURE, avant que le
  // handler AJAX Prestige ne fetch. On dedoublonne url.search in-place
  // (URLSearchParams garde l'ordre d'insertion, on filtre les cles
  // (name,value) deja vues). Couvre facets-form (clic pastille),
  // facet-link (chip x native Prestige) et hijackChipClick — un seul
  // point d'entree.
  document.addEventListener("facet:update", function (ev) {
    if (!ev.detail || !ev.detail.url) return;
    var url = ev.detail.url;
    if (!(url instanceof URL)) return;
    var params = new URLSearchParams(url.search);
    var out = new URLSearchParams();
    var seen = Object.create(null);
    var dropped = 0;
    var it = params.entries();
    var step;
    while (!(step = it.next()).done) {
      var k = step.value[0];
      var v = step.value[1];
      var key = k + "\t" + v;
      if (seen[key]) { dropped += 1; continue; }
      seen[key] = true;
      out.append(k, v);
    }
    if (dropped > 0) {
      url.search = out.toString();
      if (DEBUG) console.log("[snazzy] facet:update dedup", { dropped: dropped });
    }
  }, true);

  // ==== MANIFEST (metaobjet) =============================================
  function loadManifest() {
    var el = document.getElementById("snazzy-facets-manifest");
    if (!el) return { couleur: { param: "filter.v.option.couleur", groupes: [] }, style: { param: "filter.p.m.custom.style_mots_cles", libelles: [] } };
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      console.warn("[snazzy] manifest parse error", e);
      return { couleur: { param: "filter.v.option.couleur", groupes: [] }, style: { param: "filter.p.m.custom.style_mots_cles", libelles: [] } };
    }
  }

  // ==== URL PARAMS (source de vérité) ====================================
  function urlValues(paramName) {
    var out = [];
    var q = location.search.replace(/^\?/, "");
    if (!q) return out;
    var pairs = q.split("&");
    for (var i = 0; i < pairs.length; i += 1) {
      var kv = pairs[i].split("=");
      if (kv.length !== 2) continue;
      if (decodeURIComponent(kv[0]) !== paramName) continue;
      try { out.push(decodeURIComponent(kv[1].replace(/\+/g, " "))); } catch (_) { /* skip */ }
    }
    return out;
  }

  function rebuildUrlWithout(paramName, valuesToRemove) {
    var url = new URL(location.href);
    var params = new URLSearchParams(url.search);
    var out = new URLSearchParams();
    var removeSet = Object.create(null);
    for (var i = 0; i < valuesToRemove.length; i += 1) removeSet[valuesToRemove[i]] = true;
    // Dedupe : on ne conserve qu'une seule occurrence de chaque (name, value).
    // Retire aussi les doublons qui traînaient éventuellement dans l'URL source.
    var seen = Object.create(null);
    var it = params.entries();
    var step;
    while (!(step = it.next()).done) {
      var k = step.value[0];
      var v = step.value[1];
      if (k === paramName && removeSet[v]) continue;
      var seenKey = k + " " + v;
      if (seen[seenKey]) continue;
      seen[seenKey] = true;
      out.append(k, v);
    }
    url.search = out.toString();
    return url;
  }

  // ==== SYNC INPUTS FROM URL =============================================
  function syncInputsFromUrl(manifest) {
    var urlC = urlValues(manifest.couleur.param);
    var urlS = urlValues(manifest.style.param);
    var setC = new Set(urlC);
    var setS = new Set(urlS);
    var inputs = document.querySelectorAll(SELECTOR_VALUE);
    var changedC = 0, changedS = 0;
    for (var i = 0; i < inputs.length; i += 1) {
      var el = inputs[i];
      var desired;
      if (el.name === manifest.couleur.param) {
        desired = setC.has(el.value);
        if (el.checked !== desired) { el.checked = desired; changedC += 1; }
      } else if (el.name === manifest.style.param) {
        desired = setS.has(el.value);
        if (el.checked !== desired) { el.checked = desired; changedS += 1; }
      }
    }
    log("syncInputsFromUrl", { urlC: urlC.length, urlS: urlS.length, changedC: changedC, changedS: changedS });
    return { urlC: urlC, urlS: urlS };
  }

  // ==== RESYNC PASTILLES (règle : tous les inputs cochés) ================
  function setActive(groupe, active) {
    groupe.classList.toggle("is-active", active);
    var btn = groupe.querySelector(SELECTOR_BTN);
    if (btn) btn.setAttribute("aria-pressed", active ? "true" : "false");
  }

  function resyncSection(selector, label) {
    var groupes = document.querySelectorAll(selector);
    var debugRows = [];
    for (var i = 0; i < groupes.length; i += 1) {
      var g = groupes[i];
      var inputs = g.querySelectorAll(SELECTOR_VALUE);
      if (inputs.length === 0) { setActive(g, false); continue; }
      var checked = 0;
      for (var j = 0; j < inputs.length; j += 1) {
        if (inputs[j].checked) checked += 1;
      }
      var actif = checked === inputs.length;
      setActive(g, actif);
      if (DEBUG) {
        debugRows.push({
          nom: g.getAttribute("data-snazzy-groupe") || g.getAttribute("data-snazzy-style-libelle") || "?",
          raw: inputs.length,
          cochés: checked,
          actif: actif ? "✓" : "·",
        });
      }
    }
    if (DEBUG && debugRows.length > 0 && typeof console.table === "function") {
      console.groupCollapsed("[snazzy] resync " + label);
      console.table(debugRows);
      console.groupEnd();
    }
  }

  function resyncAll() {
    resyncSection(".snazzy-groupe.snazzy-batch-group", "couleur");
    resyncSection(".snazzy-style-groupe.snazzy-batch-group", "style");
  }

  // ==== CHIPS (client-side = source unique) =============================
  // On SUPPRIME toutes les chips MOTUS existantes (server + JS précédent)
  // et on rebuild depuis le manifest + URL. Ainsi Shopify.active_values
  // (peu fiable) ne pollue pas.
  function renderChipsClientSide(manifest) {
    var urlC = urlValues(manifest.couleur.param);
    var urlS = urlValues(manifest.style.param);

    var container = document.querySelector(".snazzy-active-facets");
    if (!container && urlC.length === 0 && urlS.length === 0) return;
    if (!container) {
      var main = document.querySelector(".collection__main");
      if (!main) return;
      container = document.createElement("div");
      container.className = "active-facets snazzy-active-facets";
      main.insertBefore(container, main.firstChild);
    }

    // 1) SUPPRIME les chips MOTUS existantes (serveur + précédent JS)
    container.querySelectorAll(".snazzy-active-groupe, .snazzy-active-style, .snazzy-active-orphelins").forEach(function (el) {
      el.remove();
    });

    if (urlC.length === 0 && urlS.length === 0) {
      // Rien d'actif → on peut nettoyer aussi le "Clear all" MOTUS et masquer
      container.querySelectorAll(".facets-clear-all").forEach(function (el) { el.remove(); });
      return;
    }

    // 2) Détermine groupes/libellés pleinement actifs
    var setC = new Set(urlC);
    var setS = new Set(urlS);
    var couvertsC = new Set();
    var couvertsS = new Set();
    var actifsC = [];
    manifest.couleur.groupes.forEach(function (g) {
      if (!g.valeurs || g.valeurs.length === 0) return;
      if (g.valeurs.every(function (v) { return setC.has(v); })) {
        actifsC.push(g);
        g.valeurs.forEach(function (v) { couvertsC.add(v); });
      }
    });
    var actifsS = [];
    manifest.style.libelles.forEach(function (fs) {
      if (!fs.tokens || fs.tokens.length === 0) return;
      if (fs.tokens.every(function (t) { return setS.has(t); })) {
        actifsS.push(fs);
        fs.tokens.forEach(function (t) { couvertsS.add(t); });
      }
    });
    var orphelinsC = urlC.filter(function (v) { return !couvertsC.has(v); });
    var orphelinsS = urlS.filter(function (v) { return !couvertsS.has(v); });

    log("chips client-side rebuild", {
      actifsC: actifsC.map(function (g) { return g.nom; }),
      actifsS: actifsS.map(function (fs) { return fs.libelle; }),
      orphelinsC: orphelinsC.length,
      orphelinsS: orphelinsS.length,
    });

    // 3) Emit chips
    function svgClose() {
      return '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';
    }
    actifsC.forEach(function (g) {
      var chip = document.createElement("div");
      chip.className = "removable-facet snazzy-active-groupe";
      chip.setAttribute("data-snazzy-groupe", g.nom);
      chip.setAttribute("data-snazzy-client-side", "1");
      // Pastille : identique au drawer (hex OU classe motif via handle).
      var pastilleClass = "snazzy-active-pastille";
      var pastilleStyle = "";
      if (g.motif) {
        pastilleClass += " snazzy-pastille--motif snazzy-pastille--" + (g.handle || "");
      } else if (g.pastille) {
        pastilleStyle = ' style="background: ' + g.pastille.replace(/"/g, "") + ';"';
      }
      chip.innerHTML = '<a href="#" class="tap-area link-faded" data-no-instant aria-label="Retirer ' + g.nom + '">' + svgClose() + '</a><span class="' + pastilleClass + '"' + pastilleStyle + ' aria-hidden="true"></span><span class="text-subdued">' + g.nom + '</span>';
      container.appendChild(chip);
    });
    actifsS.forEach(function (fs) {
      var chipS = document.createElement("div");
      chipS.className = "removable-facet snazzy-active-style";
      chipS.setAttribute("data-snazzy-style-libelle", fs.libelle);
      chipS.setAttribute("data-snazzy-client-side", "1");
      chipS.innerHTML = '<a href="#" class="tap-area link-faded" data-no-instant aria-label="Retirer ' + fs.libelle + '">' + svgClose() + '</a><span class="text-subdued">' + fs.libelle + '</span>';
      container.appendChild(chipS);
    });
    if (orphelinsC.length > 0) {
      var oC = document.createElement("div");
      oC.className = "removable-facet snazzy-active-orphelins";
      oC.setAttribute("data-snazzy-orphelins-couleur", orphelinsC.length);
      oC.setAttribute("data-snazzy-client-side", "1");
      oC.innerHTML = '<a href="#" class="tap-area link-faded" data-no-instant aria-label="Retirer autres nuances">' + svgClose() + '</a><span class="text-subdued">Autres nuances (' + orphelinsC.length + ')</span>';
      container.appendChild(oC);
    }
    if (orphelinsS.length > 0) {
      var oS = document.createElement("div");
      oS.className = "removable-facet snazzy-active-orphelins";
      oS.setAttribute("data-snazzy-orphelins-style", orphelinsS.length);
      oS.setAttribute("data-snazzy-client-side", "1");
      oS.innerHTML = '<a href="#" class="tap-area link-faded" data-no-instant aria-label="Retirer autres styles">' + svgClose() + '</a><span class="text-subdued">Autres styles (' + orphelinsS.length + ')</span>';
      container.appendChild(oS);
    }

    container.style.display = "";
  }

  // ==== HIJACK CHIP CLICK ================================================
  // Verrou anti-double-clic : une fois qu'un × chip a émis facet:update,
  // on ignore tout nouveau clic sur une chip jusqu'à ce que le re-render
  // MutationObserver ré-appelle initialSync (déverrouille). Sans ça, un
  // double-clic rapide envoie 2 fetch AJAX concurrents à Prestige.
  var _chipClickLock = false;
  function releaseChipClickLock() { _chipClickLock = false; }

  function hijackChipClick(manifest) {
    document.addEventListener("click", function (ev) {
      var chip = ev.target.closest(".snazzy-active-groupe, .snazzy-active-style, .snazzy-active-orphelins");
      if (!chip) return;
      var link = ev.target.closest("a");
      if (!link) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (_chipClickLock) {
        log("hijack chip click BLOCKED (lock)");
        return;
      }
      var toRemove = [];
      var paramName = null;
      if (chip.classList.contains("snazzy-active-groupe")) {
        paramName = manifest.couleur.param;
        var nom = chip.getAttribute("data-snazzy-groupe");
        var g = manifest.couleur.groupes.find(function (x) { return x.nom === nom; });
        if (g) {
          // Retrait chip groupe COULEUR en 2 étages :
          // 1) Retire les valeurs de g QUI NE SONT PAS protégées par un
          //    autre groupe fully active (préserve les autres chips).
          // 2) Balaie l'URL résultante : toute valeur qui n'appartient
          //    plus à aucun groupe fully active est retirée aussi (élimine
          //    les fantômes "Autres nuances" issus des retraits cascade).
          var urlValsC = new Set(urlValues(paramName));
          var protectedC = new Set();
          manifest.couleur.groupes.forEach(function (h) {
            if (h.nom === g.nom) return;
            if (!h.valeurs || h.valeurs.length === 0) return;
            var stillActive = h.valeurs.every(function (v) { return urlValsC.has(v); });
            if (!stillActive) return;
            h.valeurs.forEach(function (v) { protectedC.add(v); });
          });
          // Étape 1 : simule le retrait des valeurs non-protégées.
          var afterUrl = new Set(urlValsC);
          g.valeurs.forEach(function (v) {
            if (!protectedC.has(v)) afterUrl.delete(v);
          });
          // Étape 2 : nettoie les orphelins résultants — valeurs qui
          // n'appartiennent plus à AUCUN groupe fully active dans afterUrl.
          var coveredAfter = new Set();
          manifest.couleur.groupes.forEach(function (h) {
            if (h.nom === g.nom) return;
            if (!h.valeurs || h.valeurs.length === 0) return;
            var fullyActiveAfter = h.valeurs.every(function (v) { return afterUrl.has(v); });
            if (fullyActiveAfter) h.valeurs.forEach(function (v) { coveredAfter.add(v); });
          });
          // toRemove = urlValsC courant - (afterUrl ∩ coveredAfter).
          // càd on retire tout ce qui a été retiré en étape 1 + tout ce
          // qui reste dans afterUrl mais devient orphelin.
          var finalKeep = new Set();
          afterUrl.forEach(function (v) { if (coveredAfter.has(v)) finalKeep.add(v); });
          toRemove = Array.from(urlValsC).filter(function (v) { return !finalKeep.has(v); });
        }
      } else if (chip.classList.contains("snazzy-active-style")) {
        paramName = manifest.style.param;
        var lib = chip.getAttribute("data-snazzy-style-libelle");
        var fs = manifest.style.libelles.find(function (x) { return x.libelle === lib; });
        if (fs) {
          // Retrait chip STYLE : même stratégie 2-étages.
          var urlValsS = new Set(urlValues(paramName));
          var protectedS = new Set();
          manifest.style.libelles.forEach(function (fh) {
            if (fh.libelle === fs.libelle) return;
            if (!fh.tokens || fh.tokens.length === 0) return;
            var stillActiveS = fh.tokens.every(function (t) { return urlValsS.has(t); });
            if (!stillActiveS) return;
            fh.tokens.forEach(function (t) { protectedS.add(t); });
          });
          var afterUrlS = new Set(urlValsS);
          fs.tokens.forEach(function (t) { if (!protectedS.has(t)) afterUrlS.delete(t); });
          var coveredAfterS = new Set();
          manifest.style.libelles.forEach(function (fh) {
            if (fh.libelle === fs.libelle) return;
            if (!fh.tokens || fh.tokens.length === 0) return;
            var fullyActiveAfterS = fh.tokens.every(function (t) { return afterUrlS.has(t); });
            if (fullyActiveAfterS) fh.tokens.forEach(function (t) { coveredAfterS.add(t); });
          });
          var finalKeepS = new Set();
          afterUrlS.forEach(function (t) { if (coveredAfterS.has(t)) finalKeepS.add(t); });
          toRemove = Array.from(urlValsS).filter(function (t) { return !finalKeepS.has(t); });
        }
      } else if (chip.classList.contains("snazzy-active-orphelins")) {
        // Orphelins : retire toutes les valeurs URL non-couvertes par un groupe/libellé actif
        var isCouleur = chip.hasAttribute("data-snazzy-orphelins-couleur");
        paramName = isCouleur ? manifest.couleur.param : manifest.style.param;
        var urlVals = urlValues(paramName);
        var couverts = new Set();
        if (isCouleur) {
          manifest.couleur.groupes.forEach(function (g0) {
            var allIn = g0.valeurs.every(function (v) { return urlVals.indexOf(v) >= 0; });
            if (allIn) g0.valeurs.forEach(function (v) { couverts.add(v); });
          });
        } else {
          manifest.style.libelles.forEach(function (fs0) {
            var allIn = fs0.tokens.every(function (t) { return urlVals.indexOf(t) >= 0; });
            if (allIn) fs0.tokens.forEach(function (t) { couverts.add(t); });
          });
        }
        toRemove = urlVals.filter(function (v) { return !couverts.has(v); });
      }
      log("hijack chip click", { paramName: paramName, toRemove: toRemove.length });
      if (!paramName || toRemove.length === 0) return;
      var newUrl = rebuildUrlWithout(paramName, toRemove);
      // Utilise l'événement facet:update de Prestige pour rester en AJAX
      var section = document.querySelector('[id^="shopify-section-"] .collection, [id^="shopify-section-"][id$="__main"]');
      var sectionId = null;
      if (section) {
        var wrap = section.closest('[id^="shopify-section-"]');
        if (wrap) sectionId = wrap.id.replace(/^shopify-section-/, "");
      }
      if (sectionId) {
        newUrl.searchParams.set("section_id", sectionId);
        _chipClickLock = true;
        // Filet de sécurité si le MO n'arrive pas (ex. erreur AJAX Prestige)
        setTimeout(releaseChipClickLock, 5000);
        document.dispatchEvent(new CustomEvent("facet:update", { bubbles: true, detail: { url: newUrl } }));
      } else {
        _chipClickLock = true;
        location.href = newUrl.toString();
      }
    }, true);
  }

  // ==== DEDUPE (URL sans doublons) =======================================
  // Quand plusieurs groupes partagent des valeurs (ex "Ambre" ∈ Blanc ∩
  // Doré), chaque fieldset a son PROPRE input avec la même value=. Après
  // batchToggle, tous ces inputs sont cochés → form submit envoie N fois
  // le même filter.v.option.couleur=Ambre. Sans effet sur le filtrage
  // Shopify mais URL enfle (22 187 chars pour Blanc+Bleu+Rose ≈ 246
  // valeurs uniques × ~2 en moyenne = 504 params).
  //
  // Solution : garder UN SEUL input.checked par (name, value). Les autres
  // instances (dans les autres fieldsets) sont temporairement décochées
  // pour la soumission. syncInputsFromUrl re-cochera tous les inputs
  // correspondants après le round-trip AJAX.
  function dedupeCheckedInputs() {
    var seen = Object.create(null);
    var all = document.querySelectorAll(SELECTOR_VALUE);
    var duped = 0;
    for (var i = 0; i < all.length; i += 1) {
      var el = all[i];
      if (!el.checked) continue;
      var key = el.name + " " + el.value;
      if (seen[key]) {
        el.checked = false;
        duped += 1;
      } else {
        seen[key] = true;
      }
    }
    if (duped > 0) log("dedupe", { retirés: duped });
  }

  // ==== BATCH TOGGLE (clic pastille) =====================================
  function batchToggle(button) {
    var groupe = button.closest(SELECTOR_GROUPE);
    if (!groupe) return;
    // Ignore les libellés style grisés (aria-disabled par updateDisponibilite)
    if (groupe.classList.contains("snazzy-indisponible") || button.getAttribute("aria-disabled") === "true") return;
    var inputs = groupe.querySelectorAll(SELECTOR_VALUE);
    if (inputs.length === 0) return;
    var allChecked = true;
    for (var i = 0; i < inputs.length; i += 1) {
      if (!inputs[i].checked) { allChecked = false; break; }
    }
    var nextState = !allChecked;
    var nom = groupe.getAttribute("data-snazzy-groupe") || groupe.getAttribute("data-snazzy-style-libelle") || "?";

    // RETRAIT (nextState=false) : suit le même chemin que hijackChipClick,
    // càd calcule l'URL finale propre (toKeep = union groupes restants
    // fully active) et dispatch facet:update. Sinon, le form submit
    // natif enverrait uniquement les inputs de CE fieldset décochés,
    // laissant les valeurs partagées par d'autres groupes/fieldsets
    // toujours dans l'URL — source de la chip "Autres nuances" fantôme.
    if (!nextState) {
      var manifest = getManifest();
      if (manifest) {
        var isStyleGroup = groupe.classList.contains("snazzy-style-groupe");
        var paramName = isStyleGroup ? manifest.style.param : manifest.couleur.param;
        var urlValsB = new Set(urlValues(paramName));
        var pool = isStyleGroup ? manifest.style.libelles : manifest.couleur.groupes;
        var keyValues = isStyleGroup ? "tokens" : "valeurs";
        var keyName = isStyleGroup ? "libelle" : "nom";
        var meObj = pool.find(function (h) { return h[keyName] === nom; });
        var meVals = meObj ? (meObj[keyValues] || []) : [];
        // Stratégie 2-étages identique à hijackChipClick.
        var protectedB = new Set();
        pool.forEach(function (h) {
          if (h[keyName] === nom) return;
          var vs = h[keyValues];
          if (!vs || vs.length === 0) return;
          var stillActive = vs.every(function (v) { return urlValsB.has(v); });
          if (!stillActive) return;
          vs.forEach(function (v) { protectedB.add(v); });
        });
        var afterUrlB = new Set(urlValsB);
        meVals.forEach(function (v) { if (!protectedB.has(v)) afterUrlB.delete(v); });
        var coveredAfterB = new Set();
        pool.forEach(function (h) {
          if (h[keyName] === nom) return;
          var vs = h[keyValues];
          if (!vs || vs.length === 0) return;
          var fullyActiveAfter = vs.every(function (v) { return afterUrlB.has(v); });
          if (fullyActiveAfter) vs.forEach(function (v) { coveredAfterB.add(v); });
        });
        var finalKeepB = new Set();
        afterUrlB.forEach(function (v) { if (coveredAfterB.has(v)) finalKeepB.add(v); });
        var toRemoveB = Array.from(urlValsB).filter(function (v) { return !finalKeepB.has(v); });
        if (toRemoveB.length > 0) {
          for (var k = 0; k < inputs.length; k += 1) inputs[k].checked = false;
          setActive(groupe, false);
          log("click pastille (retrait via facet:update propre)", { groupe: nom, retirés: toRemoveB.length });
          var newUrl = rebuildUrlWithout(paramName, toRemoveB);
          var section = document.querySelector('[id^="shopify-section-"] .collection, [id^="shopify-section-"][id$="__main"]');
          var sectionId = null;
          if (section) {
            var wrap = section.closest('[id^="shopify-section-"]');
            if (wrap) sectionId = wrap.id.replace(/^shopify-section-/, "");
          }
          if (sectionId) {
            newUrl.searchParams.set("section_id", sectionId);
            _chipClickLock = true;
            setTimeout(releaseChipClickLock, 5000);
            document.dispatchEvent(new CustomEvent("facet:update", { bubbles: true, detail: { url: newUrl } }));
          } else {
            _chipClickLock = true;
            location.href = newUrl.toString();
          }
          return;
        }
      }
    }

    // AJOUT (nextState=true) : chemin natif — coche les inputs du fieldset
    // et laisse le form submit Prestige gérer. C'est ce qui garantit
    // l'ajout des valeurs à l'URL et le AJAX section swap.
    for (var j = 0; j < inputs.length; j += 1) {
      inputs[j].checked = nextState;
    }
    setActive(groupe, nextState);
    var form = button.closest(SELECTOR_FORM);
    if (!form) return;
    // NB : pas de dedupe sur les inputs (cassait l'etat entre 2 formes
    // dupliquees). La dedup se fait au niveau URL via l'interceptor
    // capture-phase de facet:update ci-dessus — un seul point d'entree.
    log("click pastille", { groupe: nom, nextState: nextState, nbInputs: inputs.length });
    form.dispatchEvent(new Event("change", { bubbles: true, cancelable: true }));
  }

  document.addEventListener("click", function (ev) {
    var target = ev.target;
    if (!(target instanceof Element)) return;
    var btn = target.closest(SELECTOR_BTN);
    if (!btn) return;
    ev.preventDefault();
    batchToggle(btn);
  });

  // ==== INIT + OBSERVER ==================================================
  var _manifestCache = null;
  function getManifest() {
    if (!_manifestCache) _manifestCache = loadManifest();
    return _manifestCache;
  }

  // ==== DISPONIBILITÉ (grisage/masquage 3-états) =========================
  // manifest.reglages.mode_indispo : "toujours_visible" | "grise" | "masque".
  // Fallback : afficher_indisponibles == false → "masque", sinon "grise".
  // RÈGLE ABSOLUE (garde-fou UX) : un élément avec .is-active (via input
  // check OU classe posée par resyncAll) N'EST JAMAIS ni grisé ni masqué,
  // pour permettre le retrait du filtre.
  function modeIndispoFrom(manifest) {
    var r = manifest && manifest.reglages;
    if (!r) return "masque";
    var raw = String(r.mode_indispo || "").toLowerCase();
    if (raw === "toujours_visible" || raw === "grise" || raw === "masque") return raw;
    return r.afficher_indisponibles === false ? "masque" : "grise";
  }
  function updateDisponibilite(manifest) {
    var mode = modeIndispoFrom(manifest);
    var els = document.querySelectorAll(".snazzy-disponibilite");
    for (var i = 0; i < els.length; i += 1) {
      var el = els[i];
      var count = parseInt(el.getAttribute("data-count") || "0", 10);
      // Garde-fou : si l'élément est ACTIF (input coché OU classe .is-active),
      // on ne le touche jamais — sinon l'utilisateur ne peut plus retirer.
      var input = el.querySelector('input[type="checkbox"]');
      var isActive = el.classList.contains("is-active") || (input && input.checked);
      var indispo = !(count > 0) && !isActive;
      if (indispo && mode !== "toujours_visible") {
        if (mode === "masque") {
          el.classList.add("snazzy-masque");
          el.classList.remove("snazzy-indisponible");
        } else {
          el.classList.add("snazzy-indisponible");
          el.classList.remove("snazzy-masque");
        }
        if (input) {
          input.disabled = true;
          input.setAttribute("aria-disabled", "true");
        }
        var btn = el.querySelector(".snazzy-style-btn, .snazzy-pastille-btn");
        if (btn) {
          btn.setAttribute("aria-disabled", "true");
          btn.setAttribute("tabindex", "-1");
        }
      } else {
        el.classList.remove("snazzy-indisponible", "snazzy-masque");
        if (input) { input.disabled = false; input.removeAttribute("aria-disabled"); }
        var btn2 = el.querySelector(".snazzy-style-btn, .snazzy-pastille-btn");
        if (btn2) { btn2.removeAttribute("aria-disabled"); btn2.removeAttribute("tabindex"); }
      }
    }
  }

  // ==== COMPTEUR "Filter (n)" ============================================
  // Recompte selon la MÊME règle que les chips :
  // - n groupes couleur pleinement actifs (1 chip par groupe)
  // - + 1 orphelin couleur si présent
  // - n libellés style pleinement actifs (1 chip par libellé)
  // - + 1 orphelin style si présent
  // - + n valeurs Taille actives (une par valeur)
  // - + n valeurs Catégorie actives (une par valeur)
  // - + éventuelles autres facettes prises en charge par snazzy-active-facets
  function updateFilterButtonCount() {
    var container = document.querySelector(".snazzy-active-facets");
    var nbFacets = 0;
    if (container) {
      nbFacets = container.querySelectorAll(".removable-facet").length;
    }
    var buttons = document.querySelectorAll("button.collection-toolbar__button");
    buttons.forEach(function (btn) {
      // Walk DFS pour trouver le TextNode contenant "Filter (n)" ou "Filter"
      var target = null;
      var walker = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        var txt = (node.textContent || "").trim();
        if (/^(Filter|Filtres?|Filtrer)/i.test(txt)) {
          target = node;
          break;
        }
      }
      if (!target) return;
      var raw = target.textContent || "";
      var match = raw.match(/^(\s*)(Filter|Filtres?|Filtrer)([^(\n]*?)(\s*\(\d+\))?(\s*)$/i);
      if (!match) return;
      var lead = match[1] || "";
      var label = match[2];
      var mid = (match[3] || "").replace(/\s+$/, "");
      var trail = match[5] || "";
      var newSuffix = nbFacets > 0 ? " (" + nbFacets + ")" : "";
      target.textContent = lead + label + mid + newSuffix + trail;
    });
    log("filter button count", { nbFacets: nbFacets });
  }

  // ==== GRISAGE COULEURS (client-side, page courante) ====================
  // Approche : on lit les groupes réellement représentés parmi les cartes
  // produit AFFICHÉES (data-snazzy-groupes émis par snazzy-product-attrs).
  // Un groupe absent de cet ensemble est grisé (opacité + non-cliquable).
  // Exceptions :
  //   1) une pastille ACTIVE reste toujours cliquable (permet le retrait
  //      même s'il ne reste aucun produit visible dans le groupe) ;
  //   2) si reglages.afficher_indisponibles === false → on MASQUE plutôt
  //      que de griser (classe snazzy-masque au lieu de snazzy-indisponible).
  //
  // Limite honnête : le calcul porte sur la PAGE COURANTE, pas sur l'ensemble
  // paginé. Documenté dans le README. Alternative (server-side sommation
  // par groupe) refusée : double comptage sur les valeurs multi-groupes.
  function updateDisponibiliteCouleurs(manifest) {
    var mode = modeIndispoFrom(manifest);
    // Rassemble les groupes représentés
    var represented = new Set();
    var cards = document.querySelectorAll("[data-snazzy-groupes]");
    for (var i = 0; i < cards.length; i += 1) {
      var raw = cards[i].getAttribute("data-snazzy-groupes") || "";
      if (!raw) continue;
      var parts = raw.split("|");
      for (var j = 0; j < parts.length; j += 1) {
        var g = parts[j];
        if (g) represented.add(g);
      }
    }
    // Ne fait rien si aucune carte n'expose l'attribut (thème non déployé
    // ou template ne rendant pas de cartes produit) — évite de tout griser
    // par erreur sur un état de chargement transitoire.
    if (cards.length === 0) return;

    var groupes = document.querySelectorAll(".snazzy-groupe.snazzy-batch-group");
    for (var k = 0; k < groupes.length; k += 1) {
      var el = groupes[k];
      var nom = el.getAttribute("data-snazzy-groupe");
      // Garde-fou : actif → jamais grisé ni masqué (sinon plus de retrait).
      var active = el.classList.contains("is-active");
      var indispo = !!nom && !represented.has(nom) && !active;
      var btn = el.querySelector(".snazzy-pastille-btn");
      if (indispo && mode !== "toujours_visible") {
        if (mode === "masque") {
          el.classList.add("snazzy-masque");
          el.classList.remove("snazzy-indisponible");
        } else {
          el.classList.add("snazzy-indisponible");
          el.classList.remove("snazzy-masque");
        }
        if (btn) {
          btn.setAttribute("aria-disabled", "true");
          btn.setAttribute("tabindex", "-1");
        }
      } else {
        el.classList.remove("snazzy-indisponible", "snazzy-masque");
        if (btn) {
          btn.removeAttribute("aria-disabled");
          btn.removeAttribute("tabindex");
        }
      }
    }
  }

  function initialSync() {
    var mf = getManifest();
    syncInputsFromUrl(mf);
    resyncAll();
    renderChipsClientSide(mf);
    updateDisponibilite(mf);
    updateDisponibiliteCouleurs(mf);
    updateFilterButtonCount();
    // Le re-render section est terminé : les futurs × chips redeviennent
    // actifs. Sans ce release, le verrou anti-double-clic resterait posé.
    releaseChipClickLock();
  }

  // Post-swap AJAX Prestige (section replacée)
  var mo = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i += 1) {
      var m = mutations[i];
      for (var j = 0; j < m.addedNodes.length; j += 1) {
        var n = m.addedNodes[j];
        if (n && n.nodeType === 1 && n.querySelector && n.querySelector(SELECTOR_GROUPE)) {
          _manifestCache = null; // manifest peut être re-rendu par le swap
          initialSync();
          return;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  document.documentElement.addEventListener("theme:loading:end", function () {
    _manifestCache = null;
    initialSync();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initialSync();
      hijackChipClick(getManifest());
    });
  } else {
    initialSync();
    hijackChipClick(getManifest());
  }

  // Debug panels : unhide drawer panel + toggle html.snazzy-debug-visible
  // (les panels produit-card se révèlent via .snazzy-debug-visible en CSS)
  function toggleDebugPanel() {
    var panels = document.querySelectorAll("[data-snazzy-debug]");
    for (var i = 0; i < panels.length; i += 1) {
      if (DEBUG) panels[i].removeAttribute("hidden");
      else panels[i].setAttribute("hidden", "");
    }
    if (DEBUG) document.documentElement.classList.add("snazzy-debug-visible");
    else document.documentElement.classList.remove("snazzy-debug-visible");
  }
  toggleDebugPanel();
  document.documentElement.addEventListener("theme:loading:end", toggleDebugPanel);

  if (DEBUG) log("v3 loaded — debug actif");
})();
